import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight, GraduationCap, Users, ChevronDown, BookOpen, Heart, Briefcase } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPortals, setShowPortals] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Programs', path: '/programs' },
    { name: 'Enrollment', path: '/enrollment' },
    { name: 'Projects', path: '/projects' },
    { name: 'Partners', path: '/partners' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 w-full z-50 bg-[#060F22]/80 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <img 
              src="/logo.png" 
              alt="NextGen Tech Pathways" 
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`text-sm font-medium transition-colors duration-300 ${
                  isActive(link.path)
                    ? 'text-[#00FFE0]'
                    : 'text-[#94A3B8] hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            {/* Portal Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowPortals(!showPortals)}
                onBlur={() => setTimeout(() => setShowPortals(false), 200)}
                data-testid="nav-portal-btn"
                className="inline-flex items-center justify-center bg-[#0A1F44] text-[#00AEEF] font-semibold rounded-full px-5 py-2.5 border border-[#00AEEF]/30 hover:border-[#00AEEF] hover:bg-[#0A1F44]/80 transition-all duration-300 gap-2"
              >
                Login
                <ChevronDown className={`w-4 h-4 transition-transform ${showPortals ? 'rotate-180' : ''}`} />
              </button>
              
              {showPortals && (
                <div className="absolute right-0 mt-2 w-56 bg-[#0A1F44] border border-[#00AEEF]/30 rounded-xl shadow-xl overflow-hidden z-50">
                  <Link
                    to="/student/login"
                    data-testid="nav-student-login"
                    className="flex items-center gap-3 px-4 py-3 hover:bg-[#060F22] transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#00AEEF]/20 flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-[#00AEEF]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Student Portal</p>
                      <p className="text-[#94A3B8] text-xs">Access your courses</p>
                    </div>
                  </Link>
                  <Link
                    to="/parent/login"
                    data-testid="nav-parent-login"
                    className="flex items-center gap-3 px-4 py-3 hover:bg-[#060F22] transition-colors border-t border-[#00AEEF]/10"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#00FFE0]/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#00FFE0]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Parent Portal</p>
                      <p className="text-[#94A3B8] text-xs">Track progress</p>
                    </div>
                  </Link>
                  <Link
                    to="/teacher/login"
                    data-testid="nav-teacher-login"
                    className="flex items-center gap-3 px-4 py-3 hover:bg-[#060F22] transition-colors border-t border-[#00AEEF]/10"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#FF6A00]/20 flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-[#FF6A00]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Teacher Portal</p>
                      <p className="text-[#94A3B8] text-xs">Manage classes</p>
                    </div>
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/donate"
              data-testid="nav-donate-btn"
              className="inline-flex items-center justify-center bg-[#FFD700] text-[#0A1F44] font-bold rounded-full px-5 py-2.5 hover:shadow-[0_0_20px_rgba(255,215,0,0.3)] hover:-translate-y-1 transition-all duration-300 gap-2"
            >
              <Heart className="w-4 h-4" />
              Donate
            </Link>

            <Link
              to="/enrollment"
              data-testid="nav-enroll-btn"
              className="inline-flex items-center justify-center bg-[#FF6A00] text-white font-bold rounded-full px-6 py-2.5 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] hover:-translate-y-1 transition-all duration-300 gap-2"
            >
              Enroll Now
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  className={`text-base font-medium py-2 ${
                    isActive(link.path)
                      ? 'text-[#00FFE0]'
                      : 'text-[#94A3B8]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Mobile Portal Links */}
              <div className="border-t border-white/10 pt-4 mt-2">
                <p className="text-xs text-[#94A3B8] uppercase tracking-wider mb-3">Already enrolled?</p>
                <Link
                  to="/student/login"
                  onClick={() => setIsOpen(false)}
                  data-testid="mobile-student-login"
                  className="flex items-center gap-3 py-2 text-[#00AEEF]"
                >
                  <GraduationCap className="w-5 h-5" />
                  Student Login
                </Link>
                <Link
                  to="/parent/login"
                  onClick={() => setIsOpen(false)}
                  data-testid="mobile-parent-login"
                  className="flex items-center gap-3 py-2 text-[#00FFE0]"
                >
                  <Users className="w-5 h-5" />
                  Parent Login
                </Link>
              </div>

              <Link
                to="/enrollment"
                onClick={() => setIsOpen(false)}
                data-testid="mobile-enroll-btn"
                className="inline-flex items-center justify-center bg-[#FF6A00] text-white font-bold rounded-full px-6 py-3 mt-2"
              >
                Enroll Now
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
